import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { ZfInput, ZfStack, ZfTypography } from '@zafire/react';
import type { ChangeEvent, CSSProperties, FunctionComponent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DomicileDto } from '../../../api/generated/client';
import { DroppableId } from '../../../enums';
import { Draggable, Droppable } from '../drag-and-drop';

const dndListStyle: CSSProperties = {
    flex: 1,
    height: '250px',
    padding: '4px',
    overflowY: 'auto',
};
const dndListDeactivateStyle: CSSProperties = {
    opacity: 0.7,
    backgroundColor: 'var(--bg-color-danger-normal)',
};

interface IProps {
    availableItems: DomicileDto[];
    excluded: DomicileDto[];
    included: DomicileDto[];
    global: boolean;
    onItemsChange: (included: DomicileDto[], excluded: DomicileDto[], global: boolean) => void;
}

export const DomicileDnd: FunctionComponent<IProps> = ({ availableItems = [], excluded, included, global, onItemsChange }) => {
    const { t } = useTranslation();

    const isIncludedOrExcluded = (item: DomicileDto) => [...includedItems, ...excludedItems].some((i) => i.id === item.id);

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const [includedItems, setIncludedItems] = useState<DomicileDto[]>(included || []);
    const [excludedItems, setExcludedItems] = useState<DomicileDto[]>(excluded || []);
    const [availableDndItems, setAvailableDndItems] = useState<DomicileDto[]>(
        availableItems.filter((item) => !isIncludedOrExcluded(item)).sort((a, b) => a.countryName.localeCompare(b.countryName)),
    );

    const handleIsGlobalCheckboxClick = (event: ChangeEvent<HTMLInputElement>) => {
        onItemsChange(includedItems, excludedItems, event.target.checked);
    };

    const onFilterChange = (e: CustomEvent<string>) => {
        setAvailableDndItems(availableItems.filter((item) => !isIncludedOrExcluded(item) && item.countryName.toLowerCase().includes(e.detail.toLowerCase())));
    };

    const itemRenderFn = (item: DomicileDto) => <ZfTypography key={item.id}>{item.countryName}</ZfTypography>;

    const isSameDroppable = (itemId: number, droppableId: DroppableId) => {
        if (droppableId === DroppableId.Available) return availableDndItems.some((item) => item.id === itemId);
        if (droppableId === DroppableId.Excluded) return excludedItems.some((item) => item.id === itemId);

        return includedItems.some((item) => item.id === itemId);
    };

    const updateItemsOnDrop = (itemId: number, target: DroppableId) => {
        let item: DomicileDto;
        let newAvailable = availableDndItems;
        let newExcluded = excludedItems;
        let newIncluded = includedItems;

        if (isSameDroppable(itemId, DroppableId.Available)) {
            item = availableDndItems.find((item) => item.id === itemId)!;
            newAvailable = availableDndItems.filter((item) => item.id !== itemId);
        } else if (isSameDroppable(itemId, DroppableId.Excluded)) {
            item = excludedItems.find((item) => item.id === itemId)!;
            newExcluded = excludedItems.filter((item) => item.id !== itemId);
        } else {
            item = includedItems.find((item) => item.id === itemId)!;
            newIncluded = includedItems.filter((item) => item.id !== itemId);
        }

        if (target === DroppableId.Available) newAvailable = [...availableDndItems, item];
        if (target === DroppableId.Excluded) newExcluded = [...excludedItems, item];
        if (target === DroppableId.Included) newIncluded = [...newIncluded, item];

        setAvailableDndItems(newAvailable);
        setIncludedItems(newIncluded);
        setExcludedItems(newExcluded);
        onItemsChange(newIncluded, newExcluded, global);
    };

    const onDragStart = (event: DragStartEvent) => setActiveId(event.active.id);

    const onDragEnd = (result: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = result;

        // Dropped outside the list or in the same droppable
        if (!over || isSameDroppable(Number(active.id), over.id as DroppableId)) return;

        updateItemsOnDrop(Number(active.id), over.id as DroppableId);
    };

    return (
        <>
            <ZfStack direction="column" spacing="small">
                <ZfTypography variant="body-medium">{t('contentAdmin.domicileDndInfoText')}</ZfTypography>
                <ZfTypography>
                    <input type="checkbox" checked={global} onChange={handleIsGlobalCheckboxClick} /> {t('global')}
                </ZfTypography>
            </ZfStack>
            <ZfStack direction="row" spacing="medium">
                <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <ZfStack direction="column" spacing="medium" style={dndListStyle}>
                        <ZfTypography variant="body-medium" style={{ minHeight: 'var(--zf-input-size-min-height)', alignContent: 'center' }}>
                            {t('contentAdmin.domicileDndIncludedItems')}
                        </ZfTypography>
                        <Droppable droppableId={DroppableId.Included} style={global ? dndListDeactivateStyle : {}}>
                            {includedItems.map((item) => (
                                <Draggable key={item.id} item={item}>
                                    {itemRenderFn(item)}
                                </Draggable>
                            ))}
                        </Droppable>
                    </ZfStack>
                    <ZfStack direction="column" spacing="medium" style={dndListStyle}>
                        <ZfTypography variant="body-medium" style={{ height: 'var(--zf-input-size-min-height)', alignContent: 'center' }}>
                            {t('contentAdmin.domicileDndExcludedItems')}
                        </ZfTypography>
                        <Droppable droppableId={DroppableId.Excluded} style={!global ? dndListDeactivateStyle : {}}>
                            {excludedItems.map((item) => (
                                <Draggable key={item.id} item={item}>
                                    {itemRenderFn(item)}
                                </Draggable>
                            ))}
                        </Droppable>
                    </ZfStack>
                    <ZfStack direction="column" spacing="medium" style={dndListStyle}>
                        <ZfStack direction="row" alignment="center">
                            <ZfTypography variant="body-medium" style={{ flex: 1, minWidth: '50%' }}>
                                {t('contentAdmin.domicileDndAvailableItems')}
                            </ZfTypography>
                            <ZfInput placeholder={t('contentAdmin.dndFilter')} onInputChange={onFilterChange} />
                        </ZfStack>
                        <Droppable droppableId={DroppableId.Available}>
                            {availableDndItems.map((item) => (
                                <Draggable key={item.id} item={item}>
                                    {itemRenderFn(item)}
                                </Draggable>
                            ))}
                        </Droppable>
                    </ZfStack>
                    <DragOverlay>
                        <div className="dnd-item dnd-item-dragging">
                            {activeId
                                ? itemRenderFn(
                                      [...availableDndItems, ...excludedItems, ...includedItems].find((item) => item.id.toString() === activeId.toString())!,
                                  )
                                : null}
                        </div>
                    </DragOverlay>
                </DndContext>
            </ZfStack>
        </>
    );
};
